import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, injectIntl, intlShape} from 'react-intl';
import ReactMarkdown from 'react-markdown';
import aiIcon from '../menu-bar/icon--ai.svg';
import styles from './ai-chat.css';

const SUGGESTION_CHIPS = [
    'Make sprite jump',
    'Bounce off edges',
    'Count to 10',
    'Draw a square'
];

const AiChatComponent = props => {
    const {
        canUndo,
        isOpen,
        messages,
        isLoading,
        inputValue,
        mode,
        tokenBalance,
        onClose,
        onInputChange,
        onSendMessage,
        onClearCanvas,
        onClearMessages,
        onSuggestionClick,
        onUndo,
        onTranscribe,
        isTranscribing,
        intl
    } = props;

    const messagesEndRef = React.useRef(null);
    const mediaRecorderRef = React.useRef(null);
    const audioChunksRef = React.useRef([]);
    const [isRecording, setIsRecording] = React.useState(false);

    React.useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView(
                {behavior: 'smooth'}
            );
        }
    }, [messages, isLoading]);

    const toggleRecording = async () => {
        if (isRecording) {
            if (mediaRecorderRef.current) {
                mediaRecorderRef.current.stop();
            }
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = e => {
                if (e.data.size > 0) {
                    audioChunksRef.current.push(e.data);
                }
            };

            mediaRecorder.onstop = () => {
                setIsRecording(false);
                const mimeType = mediaRecorder.mimeType || 'audio/webm';
                const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
                
                // Convert blob to base64
                const reader = new FileReader();
                reader.readAsDataURL(audioBlob);
                reader.onloadend = () => {
                    const base64data = reader.result.split(',')[1];
                    if (onTranscribe) {
                        onTranscribe(base64data, mimeType);
                    }
                };

                // Stop all tracks to release microphone
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.start();
            setIsRecording(true);
        } catch (err) {
            console.error('Error accessing microphone', err);
            alert(intl.formatMessage({
                id: 'gui.aiChat.micError',
                defaultMessage: 'Could not access the microphone. Please check permissions.'
            }));
        }
    };

    const handleKeyDown = e => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSendMessage();
        }
    };

    if (!isOpen) return null;

    return (
        <div className={styles.aiChatContainer}>
            <div className={styles.chatHeader}>
                <div className={styles.headerLeft}>
                    <img
                        className={styles.headerIcon}
                        src={aiIcon}
                        width="22"
                        height="22"
                        alt="AI"
                    />
                    <span><FormattedMessage
                        id="gui.aiChat.askATAT"
                        defaultMessage="Ask ATAT"
                    /></span>
                    {tokenBalance !== null && (
                        <div className={styles.tokenPill}>
                            <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                            >
                                <circle
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    fill="none"
                                />
                                <text
                                    x="12"
                                    y="16"
                                    textAnchor="middle"
                                    fontSize="12"
                                    fill="currentColor"
                                >{'T'}</text>
                            </svg>
                            {tokenBalance.toLocaleString()}
                        </div>
                    )}
                </div>
                <div className={styles.headerActions}>
                    {canUndo && (
                        <button
                            className={styles.undoButton}
                            title="Undo last AI action"
                            onClick={onUndo}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="1 4 1 10 7 10" />
                                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                            </svg>
                        </button>
                    )}
                    <button
                        className={styles.clearButton}
                        title="New Chat"
                        onClick={onClearMessages}
                    >
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                            <line x1="9" y1="10" x2="15" y2="10" />
                            <line x1="12" y1="7" x2="12" y2="13" />
                        </svg>
                    </button>
                    <button
                        className={styles.clearButton}
                        title="Clear Canvas"
                        onClick={onClearCanvas}
                    >
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                    </button>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                    >
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <line
                                x1="18"
                                y1="6"
                                x2="6"
                                y2="18"
                            />
                            <line
                                x1="6"
                                y1="6"
                                x2="18"
                                y2="18"
                            />
                        </svg>
                    </button>
                </div>
            </div>

            <div className={styles.modeIndicator}>
                {mode === 'edit' ? (
                    <span className={styles.editMode}>
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{marginRight: '4px', verticalAlign: 'middle'}}
                        >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                        {'Editing blocks'}
                    </span>
                ) : (
                    <span className={styles.addMode}>
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{marginRight: '4px', verticalAlign: 'middle'}}
                        >
                            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                        </svg>
                        {'Adding blocks'}
                    </span>
                )}
            </div>

            <div className={styles.chatBody}>
                {messages.length === 0 && (
                    <div className={styles.welcomeContainer}>
                        <div className={styles.welcomeIcon}>
                            <svg
                                width="48"
                                height="48"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#CBD5E1"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                            </svg>
                        </div>
                        <p className={styles.placeholder}>
                            {'What do you want to build?'}
                        </p>
                        <div className={styles.suggestionsContainer}>
                            {SUGGESTION_CHIPS.map(chip => (
                                <button
                                    className={styles.suggestionChip}
                                    key={chip}
                                    onClick={() => onSuggestionClick(chip)}
                                >
                                    {chip}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                {messages.map((msg, index) => (
                    <div
                        className={
                            `${styles.message} ${
                                msg.role === 'user' ?
                                    styles.userMessage :
                                    styles.aiMessage
                            }`
                        }
                        key={`msg-${index}`}
                    >
                        {msg.role === 'ai' ? (
                            <ReactMarkdown className={styles.markdownContent}>{msg.content}</ReactMarkdown>
                        ) : (
                            msg.content
                        )}
                    </div>
                ))}
                {isLoading && (
                    <div
                        className={
                            `${styles.message} ` +
                            `${styles.aiMessage} ` +
                            `${styles.loadingIndicator}`
                        }
                    >
                        <div className={styles.typingIndicator}>
                            <span /><span /><span />
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            <div className={styles.chatFooter}>
                <input
                    className={styles.chatInput}
                    disabled={isLoading}
                    placeholder={intl.formatMessage({id: 'gui.aiChat.typeRequest', defaultMessage: 'Describe what you want to build...'})}
                    type="text"
                    value={inputValue}
                    onChange={onInputChange}
                    onKeyDown={handleKeyDown}
                />
                <button
                    className={`${styles.micButton} ${isRecording ? styles.recording : ''} ${isTranscribing ? styles.transcribing : ''}`}
                    onClick={toggleRecording}
                    disabled={isTranscribing}
                    title="Dictate message"
                >
                    {isTranscribing ? (
                        <div className={styles.micSpinner} />
                    ) : (
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                            <line x1="12" y1="19" x2="12" y2="23" />
                            <line x1="8" y1="23" x2="16" y2="23" />
                        </svg>
                    )}
                </button>
                <button
                    className={styles.sendButton}
                    disabled={
                        isLoading ||
                        !inputValue.trim()
                    }
                    onClick={onSendMessage}
                >
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <line
                            x1="22"
                            y1="2"
                            x2="11"
                            y2="13"
                        />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

AiChatComponent.propTypes = {
    canUndo: PropTypes.bool,
    intl: intlShape,
    inputValue: PropTypes.string,
    isLoading: PropTypes.bool,
    isOpen: PropTypes.bool,
    isRecording: PropTypes.bool,
    isTranscribing: PropTypes.bool,
    locale: PropTypes.string,
    messages: PropTypes.arrayOf(PropTypes.shape({
        role: PropTypes.string,
        content: PropTypes.string
    })),
    mode: PropTypes.string,
    tokenBalance: PropTypes.number,
    onClearCanvas: PropTypes.func,
    onClearMessages: PropTypes.func,
    onClose: PropTypes.func,
    onInputChange: PropTypes.func,
    onSendMessage: PropTypes.func,
    onSuggestionClick: PropTypes.func,
    onTranscribe: PropTypes.func,
    onUndo: PropTypes.func
};

export default injectIntl(AiChatComponent);
